import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import { INVITE_LINKS_LIVE } from '../../../core/config/invites';

/**
 * PEOPLE — the only place a store's team is managed.
 *
 * Replaces four surfaces that all did part of this: the Team column, the team
 * drawer, the Members block on Plans & Add-ons, and the invite panel.
 *
 * An invitation is a PERSON who has not accepted yet, so it is a row in the
 * same list rather than a separate screen. Before this, sending a second
 * invitation to someone was invisible — the first one lived behind a tab you
 * had to reopen to see.
 */

const ROLES = [
  { value: 'sales_admin', label: 'Team member', hint: 'Day-to-day access to stock, clients and deals.' },
  { value: 'store_admin', label: 'Store admin', hint: 'Everything a team member can do, plus settings.' },
  { value: 'owner', label: 'Owner', hint: 'Full control, including the team.' },
];

const roleLabel = (value) => ROLES.find((r) => r.value === value)?.label || value;

const initials = (name) => (name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part[0].toUpperCase())
  .join('');

/** "sent 2 days ago" without pulling in a date library for one string. */
const ago = (iso) => {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (Number.isNaN(days)) return null;
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
};

const expiresIn = (iso) => {
  if (!iso) return null;
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (Number.isNaN(days)) return null;
  return days <= 0 ? 'expired' : `expires in ${days} days`;
};

const IPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const IMail = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 6h16v12H4z" />
    <path d="M4 7l8 6 8-6" />
  </svg>
);

export function StorePeople({ storeId, seatLimit, onChanged }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const [mode, setMode] = useState('existing');
  const [role, setRole] = useState('sales_admin');

  // existing-user search
  const [query, setQuery] = useState('');
  const [found, setFound] = useState([]);

  // new person
  const [person, setPerson] = useState({ first_name: '', last_name: '', email: '', phone: '' });

  // invite
  const [invite, setInvite] = useState({ email: '', phone: '' });
  const [byPhone, setByPhone] = useState(false);

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError('');
    try {
      // Invitations are a separate endpoint but the SAME list to the reader,
      // so they are fetched together and a failure on either is one failure.
      const [m, i] = await Promise.all([
        axios.get(`/api/stores/${storeId}/members`),
        axios.get(`/api/stores/${storeId}/invites`).catch(() => ({ data: { invites: [] } })),
      ]);
      setMembers(m?.data?.members || m?.data?.items || []);
      setInvites((i?.data?.invites || i?.data?.items || []).filter((row) => !row.accepted_at && !row.revoked_at));
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load the people on this store.');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  // Search runs against every RC user, not this store's — the point is to
  // find somebody who is NOT on it yet.
  useEffect(() => {
    if (mode !== 'existing' || query.trim().length < 2) {
      setFound([]);
      return undefined;
    }

    let live = true;
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post('/api/rcusers/search', { search: query.trim(), limit: 8 });
        if (live) setFound(data?.users || data?.items || []);
      } catch {
        if (live) setFound([]);
      }
    }, 300);

    return () => { live = false; clearTimeout(timer); };
  }, [query, mode]);

  const seatsUsed = members.length;
  const overSeats = seatLimit != null && seatsUsed >= seatLimit;

  const run = useCallback(async (fn, done) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fn();
      setNotice(done);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.message || 'That did not work.');
    } finally {
      setBusy(false);
    }
  }, [load, onChanged]);

  const addExisting = (user) => run(
    () => axios.post(`/api/stores/${storeId}/members`, { user_id: user.id, role }),
    `${user.name || user.first_name || 'They'} now has access.`,
  );

  const createPerson = () => run(
    () => axios.post(`/api/stores/${storeId}/people`, { ...person, role }),
    `${person.first_name} was created and seated.`,
  ).then(() => setPerson({ first_name: '', last_name: '', email: '', phone: '' }));

  const sendInvite = () => run(
    () => axios.post(`/api/stores/${storeId}/invites`, {
      ...(byPhone ? { phone: invite.phone.trim() } : { email: invite.email.trim() }),
      role,
    }),
    byPhone ? 'Invitation created — copy the link and send it yourself.' : 'Invitation emailed.',
  ).then(() => setInvite({ email: '', phone: '' }));

  const changeRole = (member, next) => run(
    () => axios.put(`/api/stores/${storeId}/members/${member.user_id || member.id}`, { role: next }),
    'Role changed.',
  );

  const revoke = (row) => run(
    () => axios.delete(`/api/stores/${storeId}/invites/${row.id}`),
    'Invitation cancelled.',
  );

  const copyLink = async (row) => {
    const url = row.url || row.link || (row.token ? `${window.location.origin}/invite/${row.token}` : null);
    if (!url) {
      setError('That invitation has no link to copy.');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setNotice('Link copied.');
    } catch {
      setError('Could not copy — your browser blocked it.');
    }
  };

  const roleSelect = (
    <label className="rc-sc__field" style={{ flex: '1 1 160px', minWidth: 0 }}>
      <span>Role on this store</span>
      <select className="rc-sc__select" value={role} onChange={(e) => setRole(e.target.value)}>
        {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
      <p className="rc-sc__hint">{ROLES.find((r) => r.value === role)?.hint}</p>
    </label>
  );

  const seatLine = useMemo(() => {
    const seats = seatLimit == null ? `${seatsUsed} on the team` : `${seatsUsed} of ${seatLimit} seats`;
    const waiting = invites.length === 1 ? '1 invitation waiting' : `${invites.length} invitations waiting`;
    return invites.length ? `${seats} · ${waiting}` : seats;
  }, [seatsUsed, seatLimit, invites.length]);

  return (
    <div className="rc-sc__band">
      <div className="rc-sc__band-head">
        <h4>People</h4>
        <span className="rc-sc__band-count">{loading ? 'loading…' : seatLine}</span>
      </div>

      {error && <p className="rc-sc__error" role="alert">{error}</p>}
      {notice && <p className="rc-sc__hint" role="status">{notice}</p>}

      <div className="rc-sc__people">
        {!loading && members.length === 0 && invites.length === 0 && (
          <p className="rc-sc__empty">Nobody is on this store yet.</p>
        )}

        {members.map((m) => {
          const id = m.user_id || m.id;
          const name = m.name || [m.first_name, m.last_name].filter(Boolean).join(' ');

          return (
            <div className="rc-sc__person" key={`m-${id}`}>
              <span className="rc-sc__person-avatar" aria-hidden="true">{initials(name)}</span>
              <span className="rc-sc__person-body">
                <span className="rc-sc__person-name">{name || `User ${id}`}</span>
                <span className="rc-sc__person-meta">{m.phone || m.email || '—'}</span>
              </span>
              {m.is_record_owner || m.locked ? (
                <span className="rc-sc__person-fixed">Owner</span>
              ) : (
                <label>
                  <span className="rc-sc__vh">{`Role for ${name}`}</span>
                  <select
                    className="rc-sc__person-role"
                    value={m.role || 'sales_admin'}
                    disabled={busy}
                    onChange={(e) => changeRole(m, e.target.value)}
                  >
                    {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </label>
              )}
            </div>
          );
        })}

        {invites.map((row) => (
          <div className="rc-sc__person rc-sc__person--pending" key={`i-${row.id}`}>
            <span className="rc-sc__person-avatar rc-sc__person-avatar--pending" aria-hidden="true"><IMail /></span>
            <span className="rc-sc__person-body">
              <span className="rc-sc__person-name">{row.email || row.phone}</span>
              <span className="rc-sc__person-meta">
                {`Invited as ${roleLabel(row.role)}`}
                {row.delivered === false ? ' · not delivered' : ''}
                {ago(row.created_at) ? ` · sent ${ago(row.created_at)}` : ''}
                {expiresIn(row.expires_at) ? ` · ${expiresIn(row.expires_at)}` : ''}
              </span>
            </span>
            <button type="button" className="rc-sc__btn rc-sc__btn--sm" onClick={() => copyLink(row)}>Copy link</button>
            <button type="button" className="rc-sc__btn rc-sc__btn--sm rc-sc__btn--danger" disabled={busy} onClick={() => revoke(row)}>Cancel</button>
          </div>
        ))}
      </div>

      <details className="rc-sc__fold">
        <summary><IPlus /> Add someone</summary>
        <div className="rc-sc__fold-body">
          {overSeats && (
            <p className="rc-sc__note rc-sc__note--warn">
              <span>
                This store already uses all {seatLimit} of its seats. Adding another is blocked once
                enforcement is on; today it will go through.
              </span>
            </p>
          )}

          <div className="rc-sc__modes" role="group" aria-label="How to add someone">
            <button type="button" className={`rc-sc__mode ${mode === 'existing' ? 'rc-sc__mode--on' : ''}`} aria-pressed={mode === 'existing'} onClick={() => setMode('existing')}>Someone we know</button>
            <button type="button" className={`rc-sc__mode ${mode === 'new' ? 'rc-sc__mode--on' : ''}`} aria-pressed={mode === 'new'} onClick={() => setMode('new')}>New person</button>
            {INVITE_LINKS_LIVE ? (
              <button type="button" className={`rc-sc__mode ${mode === 'invite' ? 'rc-sc__mode--on' : ''}`} aria-pressed={mode === 'invite'} onClick={() => setMode('invite')}>Send an invite</button>
            ) : null}
          </div>

          {mode === 'existing' && (
            <>
              <div className="rc-sc__pair">
                <label className="rc-sc__field" style={{ flex: '999 1 240px' }}>
                  <span>Find a person</span>
                  <input
                    className="rc-sc__input"
                    type="search"
                    value={query}
                    placeholder="Name or email"
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </label>
                {roleSelect}
              </div>

              {found.length > 0 && (
                <div className="rc-sc__people">
                  {found.map((u) => {
                    const name = u.name || [u.first_name, u.last_name].filter(Boolean).join(' ');
                    const already = members.some((m) => (m.user_id || m.id) === u.id);

                    return (
                      <button
                        type="button"
                        key={u.id}
                        className="rc-sc__person"
                        style={{ textAlign: 'left', cursor: already ? 'default' : 'pointer' }}
                        disabled={busy || already}
                        onClick={() => !already && addExisting(u)}
                      >
                        <span className="rc-sc__person-avatar" aria-hidden="true">{initials(name)}</span>
                        <span className="rc-sc__person-body">
                          <span className="rc-sc__person-name">{name || `User ${u.id}`}</span>
                          <span className="rc-sc__person-meta">
                            {already ? 'already on this store' : (u.email || u.phone || `User ${u.id}`)}
                          </span>
                        </span>
                        {!already && <span className="rc-sc__person-fixed">Add</span>}
                      </button>
                    );
                  })}
                </div>
              )}

              {query.trim().length >= 2 && found.length === 0 && (
                <p className="rc-sc__hint">Nobody matches that. Use “New person” to create them.</p>
              )}
            </>
          )}

          {mode === 'new' && (
            <>
              <p className="rc-sc__note">
                <span>Creates the person and seats them straight away. They sign in with a one-time code — there is no password to set.</span>
              </p>

              <div className="rc-sc__pair">
                <label className="rc-sc__field">
                  <span>First name <span className="rc-sc__req">*</span></span>
                  <input className="rc-sc__input" value={person.first_name} onChange={(e) => setPerson({ ...person, first_name: e.target.value })} />
                </label>
                <label className="rc-sc__field">
                  <span>Last name <span className="rc-sc__req">*</span></span>
                  <input className="rc-sc__input" value={person.last_name} onChange={(e) => setPerson({ ...person, last_name: e.target.value })} />
                </label>
              </div>

              <div className="rc-sc__pair">
                <label className="rc-sc__field">
                  <span>Email address</span>
                  <input className="rc-sc__input" type="email" value={person.email} placeholder="them@theirstore.com" onChange={(e) => setPerson({ ...person, email: e.target.value })} />
                </label>
                <label className="rc-sc__field">
                  <span>Phone number</span>
                  <input className="rc-sc__input" type="tel" value={person.phone} placeholder="+44 7700 900000" onChange={(e) => setPerson({ ...person, phone: e.target.value })} />
                </label>
              </div>
              <p className="rc-sc__hint">One of email or phone is enough. The phone is what they sign in with.</p>

              <div className="rc-sc__pair">{roleSelect}</div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="rc-sc__btn rc-sc__btn--primary"
                  disabled={busy || !person.first_name.trim() || !person.last_name.trim() || (!person.email.trim() && !person.phone.trim())}
                  onClick={createPerson}
                >
                  {busy ? 'Creating…' : 'Create and seat'}
                </button>
              </div>
            </>
          )}

          {INVITE_LINKS_LIVE && mode === 'invite' && (
            <>
              <p className="rc-sc__note">
                <span>
                  They get a link and pick their own details. The seat is held until they accept.
                  Invitations last 14 days.
                </span>
              </p>

              <fieldset className="rc-sc__seg">
                <legend>How they get it</legend>
                <button type="button" className={`rc-sc__opt-btn ${!byPhone ? 'rc-sc__opt-btn--on' : ''}`} aria-pressed={!byPhone} onClick={() => setByPhone(false)}>We email it</button>
                <button type="button" className={`rc-sc__opt-btn ${byPhone ? 'rc-sc__opt-btn--on' : ''}`} aria-pressed={byPhone} onClick={() => setByPhone(true)}>Copy the link</button>
              </fieldset>

              <p className="rc-sc__hint">
                {byPhone
                  ? 'We only mint the link — send it from your own number, which is what gets opened.'
                  : 'We send the email ourselves.'}
              </p>

              <div className="rc-sc__pair">
                {byPhone ? (
                  <label className="rc-sc__field" style={{ flex: '999 1 240px' }}>
                    <span>Phone number <span className="rc-sc__req">*</span></span>
                    <input className="rc-sc__input" type="tel" value={invite.phone} placeholder="+44 7700 900000" onChange={(e) => setInvite({ ...invite, phone: e.target.value })} />
                  </label>
                ) : (
                  <label className="rc-sc__field" style={{ flex: '999 1 240px' }}>
                    <span>Email address <span className="rc-sc__req">*</span></span>
                    <input className="rc-sc__input" type="email" value={invite.email} placeholder="them@theirstore.com" onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
                  </label>
                )}
                {roleSelect}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="rc-sc__btn rc-sc__btn--primary"
                  disabled={busy || (byPhone ? !invite.phone.trim() : !invite.email.trim())}
                  onClick={sendInvite}
                >
                  {busy ? 'Sending…' : byPhone ? 'Create the link' : 'Send invitation'}
                </button>
              </div>
            </>
          )}
        </div>
      </details>
    </div>
  );
}

export default StorePeople;
